import { useEffect, useState } from 'react';
import { useDispatch } from '@witmetrics/data-store';
import type { ServiceSettings } from '@witmetrics/unison-core';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import { useIds } from '@/hooks/useIds';
import Overlay from '@/components/Overlay';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import Card from '@/components/Card';
import ScanServiceViewer from '@/components/viewers/ScanServiceViewer';
import TabsColumn from './TabsColumn';
import ServiceHeader from './ServiceHeader';
import {
  classes,
  fetchServiceData,
  fetchServiceSettings,
  getDefaultOpenIds,
} from './utils';

export default function ScanServiceOverlay() {
  const dispatch = useDispatch();
  const { onApiError, activeServiceId, onClearActiveService } = useAppState();
  const [
    fileIds,
    setFileIds,
    { toggleId: toggleFileId, addIds: addFileIds, removeIds: removeFileIds },
  ] = useIds();
  const [serviceSettings, setServiceSettings] = useState<ServiceSettings>();
  const [isFetchingData, toggleFetchingData] = useToggle(true);

  useEffect(() => {
    fetchData();
  }, [activeServiceId]);

  const fetchData = async () => {
    try {
      if (!activeServiceId) return;
      toggleFetchingData(true);
      const results = await fetchServiceData(activeServiceId);
      dispatch.addScanServices([results.service]);
      dispatch.addScanServiceFileCollections(results.serviceFileCollections);
      dispatch.addFileCollections(results.fileCollections);
      dispatch.addFileCollectionFiles(results.fileCollectionFiles);
      setFileIds(getDefaultOpenIds(results.outputFiles, results.inputFiles));
      toggleFetchingData(false);
    } catch (err) {
      onApiError(err, 'Error fetching service data', () => fetchData());
    }
  };

  const handleFetchServiceSettings = async () => {
    if (serviceSettings) return; // No need to re-fetch
    toggleFetchingData(true);
    setServiceSettings(await fetchServiceSettings(activeServiceId!));
    toggleFetchingData(false);
  };

  if (!activeServiceId) return null;

  return (
    <Overlay className={classes.overlay} onClose={onClearActiveService}>
      <ServiceHeader />
      <div className={classes.content}>
        <div className={classes.leftColumn}>
          {isFetchingData ? (
            <div className={classes.loading}>
              Loading scan service
              <LoopingEllipsis />
            </div>
          ) : (
            <TabsColumn
              isFetchingData={isFetchingData}
              openFileIds={fileIds}
              serviceSettings={serviceSettings}
              onFetchServiceSettings={handleFetchServiceSettings}
              onToggleFileId={toggleFileId}
              onOpenCollection={addFileIds}
              onCloseCollection={removeFileIds}
            />
          )}
        </div>
        <Card className={classes.viewerCard}>
          <ScanServiceViewer
            isFetchingData={isFetchingData}
            className={classes.viewer}
            openFileIds={fileIds}
          />
        </Card>
      </div>
    </Overlay>
  );
}
